import React, {FC, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {
	useNavigate,
	useSearchParams,
} from 'react-router-dom';
import {useSpring, animated} from '@react-spring/web'
import AppleBtn from "../../components/AppleBtn";

import './share.css'

interface Props {
}

const attitestChromeVideosLinks = [
	'https://attitune.aur.im/static/media/level-1.webm',
	'https://attitune.aur.im/static/media/level-1.webm',
	'https://attitune.aur.im/static/media/level-2.webm',
	'https://attitune.aur.im/static/media/level-3.webm',
	'https://attitune.aur.im/static/media/level-4.webm',
	'https://attitune.aur.im/static/media/level-5.webm',
	'https://attitune.aur.im/static/media/level-S.webm',
];
const attitestIOSVideosLinks = [
	'https://attitune.aur.im/static/media/level-1.mov',
	'https://attitune.aur.im/static/media/level-1.mov',
	'https://attitune.aur.im/static/media/level-2.mov',
	'https://attitune.aur.im/static/media/level-3.mov',
	'https://attitune.aur.im/static/media/level-4.mov',
	'https://attitune.aur.im/static/media/level-5.mov',
	'https://attitune.aur.im/static/media/level-S.mov',
];
const attitestTitles = ['Distant Attitude Vibe', 'Distant Attitude Vibe', 'Light Attitude Vibe', 'Balanced Attitude vibe', 'Harmonized Attitude vibe', 'Symbiotic Attitude vibe', 'Synergy of Core Attitudes'];
const attitestDescriptions = [
	'Vibes and attitudes feel disconnected, leading to awkward energy and emotional misalignment. Conversations lack flow, and differences dominate the interaction.',
	'Vibes and attitudes feel disconnected, leading to awkward energy and emotional misalignment. Conversations lack flow, and differences dominate the interaction.',
	'Some vibes and attitudes align, creating light-hearted conversations and occasional emotional connection. Emotional exchanges are enjoyable but lack depth.',
	'Vibes and attitudes begin to align more frequently, building trust and comfort. Conversations feel easier, and emotional exchanges are supportive.',
	'Vibes and attitudes feel synchronized, creating emotional safety and natural connection. Emotional expressions are well-received, and shared attitudes strengthen bonds.',
	'Vibes and attitudes are so aligned that interactions feel intuitive and deeply fulfilling. Emotional exchanges flow naturally, and attitudes amplify understanding and harmony.',
	'Vibes and attitudes are perfectly aligned, creating a transformative and deeply fulfilling connection. Interactions feel effortless, intuitive, and profoundly uplifting.',
]

const SharePage: FC<Props> = () => {
	const navigate = useNavigate();
	const _video: any = useRef(null);
	const [search, setSearch] = useSearchParams();
	const [images, setImages] = useState({own: '', other: ''});
	const [attituneLvl, setAttituneLvl] = useState<number>(0);
	const [isReady, setIsReady] = useState(false);
	const [isVideoOpen, setIsVideoOpen] = useState(false);

	const videoRef = useRef<HTMLVideoElement | null>(null);

	useEffect(() => {
	if (isVideoOpen && videoRef.current) {
		videoRef.current.currentTime = 0;
		videoRef.current.play().catch(() => {});
	}
	}, [isVideoOpen]);
	
	useEffect(() => {
		if (!isReady) {
			return;
		}
		
		_video.current.defaultMuted = true;
		const timer = setTimeout(() => {
			_video.current.autoplay = true;
			_video.current?.play();
		}, 2200);
		return () => {
			clearTimeout(timer);
		}
	}, [isReady]);
	
	const springImageOwn = useSpring({
		from: {x: -100, opacity: 0},
		to: {x: 0, opacity: 1},
		delay: 300,
		config: {
			duration: 1000
		}
	})
	const springImageOther = useSpring({
		from: {x: 100, opacity: 0},
		to: {x: 0, opacity: 1},
		delay: 300,
		config: {
			duration: 1000
		}
	})
	const springTitle = useSpring({
		from: {opacity: 0},
		to: {opacity: 1},
		delay: 1600,
		config: {
			duration: 1000
		}
	})
	const springVideo = useSpring({
		from: {opacity: 0},
		to: {opacity: 1},
		delay: 2100,
		config: {
			duration: 1000
		}
	})
	const springResultTitle = useSpring({
		from: {opacity: 0, y: 10},
		to: {opacity: 1, y: 0},
		delay: 3000,
		config: {
			tension: 280,
			friction: 60,
			duration: 700,
		}
	})
	const springResultText = useSpring({
		from: {opacity: 0, y: 10},
		to: {opacity: 1, y: 0},
		delay: 3500,
		config: {
			tension: 280,
			friction: 60,
			duration: 700,
		}
	})
	const springResultBtns = useSpring({
		from: {opacity: 0, y: 10},
		to: {opacity: 1, y: 0},
		delay: 4000,
		config: {
			tension: 280,
			friction: 60,
			duration: 700,
		}
	})
	
	useLayoutEffect(() => {
		const shareToken = search.get('token');
		if (!shareToken) {
			navigate('/', {replace: true});
		}
		
		fetch(`https://api.attitune.us/v1/attitests/shared/${shareToken}`)
			.then((result) => {
				if (!result.ok) {
					throw new Error("Network response was not ok");
				}
				
				return result.json();
			})
			.then((data) => {
				const {avatar_url, attitune_url, attitune_level} = data;
				if (!avatar_url || !attitune_url || attitune_level === undefined || attitune_level === null) {
					navigate('/', {replace: true});
					throw new Error("Invalid data from server");
				}
				
				setImages({own: avatar_url, other: attitune_url});
				setAttituneLvl(Number(attitune_level));

				const preload = (src: string) => new Promise<void>((resolve) => {
					const img = new Image();
					img.onload = () => resolve();
					img.onerror = () => resolve();
					img.src = src;
				});
				Promise.all([preload(avatar_url), preload(attitune_url)])
					.then(() => setIsReady(true));
			})
			.catch((err) => console.error(err));
	}, [navigate, search]);
	
	return (
		<div className="share-page">
			<div className="share-page-content">
				<div className="position-relative d-flex flex-column align-items-center">
					{isReady && (<>
						<div className="share-page-image-wrap">
							<animated.img
								style={springImageOwn}
								src={images.own}
								alt="img"
								className="share-page-image own"
							/>
							<animated.img
								style={springImageOther}
								src={images.other}
								alt="img" className="share-page-image other"
							/>
						</div>
						<animated.div className="share-page-title" style={springTitle}>Our <b>Attitune</b> is...
						</animated.div>
						<>
                            <animated.div className="share-page-video" style={springVideo}>
                                <video
                                    controls={false}
                                    width="600"
                                    height="100%"
                                    muted={true}
                                    playsInline={true}
                                    ref={_video}
                                    key={attituneLvl}
                                >
                                    <source src={attitestIOSVideosLinks[attituneLvl]} type="video/mp4; codecs=hvc1"/>
                                    <source src={attitestChromeVideosLinks[attituneLvl]} type="video/webm"/>
                                </video>
                            </animated.div>

                            <animated.div className="share-page-result-title" style={springResultTitle}>
								{attitestTitles[attituneLvl]}
                            </animated.div>
                            <animated.div className="share-page-result-text" style={springResultText}>
								{attitestDescriptions[attituneLvl]}
                            </animated.div>
                        </>
						<animated.div style={springResultBtns}>
							<AppleBtn/>
							<div className="share-page-intro" onClick={() => setIsVideoOpen(true)}>
								<div>
									<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
									<path
										opacity="0.2"
										d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
										stroke="white"
										strokeWidth="2"
									/>
									<path
										d="M10 8L16 12L10 16V8Z"
										stroke="white"
										strokeWidth="2"
									/>
									</svg>
									<span className="ml-3">What Attitune is...</span>
								</div>
							</div>
						</animated.div>
					</>)}
					{isVideoOpen && (
						<div className="video-modal">
							<div className="video-modal-overlay" onClick={() => setIsVideoOpen(false)} />
							<div className="video-modal-content">
								<div className="video-modal-player-wrap">
									<button
										className="video-modal-close"
										onClick={() => setIsVideoOpen(false)}
										aria-label="Close video"
									>
										✕
									</button>
									<video
										ref={videoRef}
										controls
										playsInline
										preload="auto"
									>
										<source
											src="https://attitune.aur.im/static/media/attitune-promo.mp4"
											type="video/mp4"
										/>
									</video>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default SharePage;